import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";

const apiUrl = process.env.REACT_APP_API_URL;

export const getMedia = createAsyncThunk(
  "media/getMedia",
  async (
    {
      page = 1,
      limit = 12,
      targetType = "",
      targetId = "",
      replace = false,
      locateId = "",
      locateUrl = "",
      restaurantId = "",
      scope = "",
    },
    thunkAPI
  ) => {
    try {
      let queryString = `limit=${limit}&page=${page}`;

      if (targetType) {
        queryString += `&targetType=${targetType}`;
      }
      if (targetId) {
        queryString += `&targetId=${targetId}`;
      }
      if (locateId) {
        queryString += `&locateId=${locateId}`;
      } else if (locateUrl) {
        queryString += `&locateUrl=${encodeURIComponent(locateUrl)}`;
      }
      if (scope === "shared" || scope === "restaurant") {
        queryString += `&scope=${scope}`;
      }
      const response = await axios.get(
        `${apiUrl}/media?${queryString}`,
        getAuthHeaders(false, restaurantId)
      );

      return {
        data: response.data.medias,
        totalCount: response.data.totalCount,
        page: response.data.page ?? page,
        limit: limit,
        replace: replace,
        locatedMediaId: response.data.locatedMediaId || null,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

export const fetchTargetTypes = createAsyncThunk(
  "media/fetchTargetTypes",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${apiUrl}/media/types`);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

export const addMedia = createAsyncThunk(
  "media/addMedia",
  async ({ formData, type, restaurantId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/media/upload?type=${type}`,
        formData,
        getAuthHeaders(true, restaurantId)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Échec de l'envoi de l'image."
      );
    }
  }
);

export const updateMedia = createAsyncThunk(
  "media/updateMedia",
  async ({ id, filename, restaurantId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/media/${id}`,
        { filename },
        getAuthHeaders(false, restaurantId)
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Erreur lors du renommage";
      return rejectWithValue(message);
    }
  }
);

export const deleteMedia = createAsyncThunk(
  "media/deleteMedia",
  async ({ id, restaurantId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/media/${id}`,
        getAuthHeaders(false, restaurantId)
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Erreur lors de la suppression";
      return rejectWithValue(message);
    }
  }
);

const mediaSlice = createSlice({
  name: "media",
  initialState: {
    items: [],
    status: "idle",
    targetTypes: [],
    error: null,
    loading: false,
    preview: null,
    currentPage: 0,
    totalCount: 0,
    hasMore: true,
    locatedMediaId: null,
    success: null,
  },
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMedia.pending, (state) => {
        state.status = "loadingMedia";
        state.loading = true;
      })
      .addCase(getMedia.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        const {
          data: newMediaItems,
          page,
          limit,
          replace,
          totalCount = 0,
          locatedMediaId,
        } = action.payload;

        if (replace || page === 1) {
          state.items = newMediaItems;
        } else {
          state.items.push(...newMediaItems);
        }

        state.currentPage = page;
        state.totalCount = Number(totalCount);
        state.hasMore = newMediaItems.length === limit;
        state.locatedMediaId = locatedMediaId;
      })
      .addCase(getMedia.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchTargetTypes.pending, (state) => {
        state.status = "loadingTypes";
        state.loading = true;
      })
      .addCase(fetchTargetTypes.fulfilled, (state, action) => {
        state.status = "fetchTypesData";
        state.loading = false;
        state.targetTypes = action.payload;
      })
      .addCase(fetchTargetTypes.rejected, (state, action) => {
        state.status = "fetchTypeError";
        state.loading = false;
        state.targetTypes = [{ value: "", label: "Tout" }];
        state.error = action.error.message;
      })
      .addCase(addMedia.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addMedia.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        const saved = action.payload;
        const docs = Array.isArray(saved) ? [...saved] : saved ? [saved] : [];
        docs.reverse().forEach((doc) => {
          if (doc?._id && !state.items.some((item) => item._id === doc._id)) {
            state.items.unshift(doc);
            state.totalCount += 1;
          }
        });
        state.success = saved?.message;
      })
      .addCase(addMedia.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updateMedia.pending, (state) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(updateMedia.fulfilled, (state, action) => {
        state.status = "updateSuccess";
        state.loading = false;
        const updated = action.payload;
        state.items = state.items.map((item) =>
          item._id === updated._id ? { ...item, ...updated } : item
        );
      })
      .addCase(updateMedia.rejected, (state, action) => {
        state.status = "updateError";
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteMedia.pending, (state) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteMedia.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        // The backend responds with just { message }, not the deleted id —
        // action.meta.arg is { id, restaurantId } for this thunk.
        state.items = state.items.filter((item) => item._id !== action.meta.arg.id);
        state.totalCount = Math.max(0, (state.totalCount || 0) - 1);
        state.success = action.payload.message;
      })
      .addCase(deleteMedia.rejected, (state, action) => {
        state.status = "deleteError";
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { updateStatus } = mediaSlice.actions;
export const selectAllMedias = (state) => state.media.items;
export const selectTargetTypes = (state) => state.media.targetTypes;
export const selectCurrentPage = (state) => state.media.currentPage;
export const selectTotalCount = (state) => state.media.totalCount;
export const selectHasMore = (state) => state.media.hasMore;
export const selectLocatedMediaId = (state) => state.media.locatedMediaId;
export const getMediaStatus = (state) => state.media.status;
export const getMediasError = (state) => state.media.error;
export const getMediaSuccess = (state) => state.media.success;
export const getMediasLoading = (state) => state.media.loading;
export default mediaSlice.reducer;
